import { Check } from "lucide-react";
import { cn } from "@weco/ui";
import { ACTION_DESCRIPTIONS } from "../../../modules/assistant/components/ActionConfirmation";
import type { ConfirmationRequest } from "../../../modules/assistant/types/chat";

/** `create_event` → "Create event" — bold summary line for the card. */
function humanizeToolName(toolName: string): string {
  const words = toolName.split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export type ProposalOutcome = "PENDING" | "CONFIRMED" | "DISMISSED";

interface ProposalCardProps {
  confirmation: ConfirmationRequest;
  outcome: ProposalOutcome;
  onConfirm: () => void;
  onReject: () => void;
}

/**
 * Kit Appendix E `ProposalCard` — the Confirm/Not-now re-skin of the existing
 * human-in-the-loop action-confirmation flow, for the companion popover.
 */
export function ProposalCard({ confirmation, outcome, onConfirm, onReject }: ProposalCardProps) {
  const descFn = ACTION_DESCRIPTIONS[confirmation.tool_name];
  const description = descFn ? descFn(confirmation.input) : `Execute: ${confirmation.tool_name}`;

  return (
    <div className="rounded-3xl bg-white/80 ring-1 ring-inset ring-blue-500/20 p-3 space-y-2">
      <div>
        <p className="text-sm font-semibold text-slate-800">{humanizeToolName(confirmation.tool_name)}</p>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>

      {outcome === "PENDING" ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className={cn(
              "inline-flex items-center gap-1 rounded-full bg-white/70 px-3 py-1.5 text-xs font-medium text-slate-700",
              "ring-1 ring-inset ring-slate-200 hover:bg-white transition-colors",
            )}
          >
            <Check className="h-3.5 w-3.5" />
            Confirm
          </button>
          <button
            type="button"
            onClick={onReject}
            className="rounded-full px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 transition-colors"
          >
            Not now
          </button>
        </div>
      ) : (
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium",
            outcome === "CONFIRMED" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500",
          )}
        >
          {outcome === "CONFIRMED" ? "Done" : "Dismissed"}
        </span>
      )}
    </div>
  );
}
